import {inject, Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {Subject} from 'rxjs';
import {WsConnectorServiceBase} from './ws-connector-base.service';
import {DemoPushType} from '../enums/demo-push-type.enum';
import {WsDemoPushDto} from '../models/interfaces/ws-demo-push-dto.interface';
import {ScenarioActions} from '../../features/scenarios/state/scenario.actions';

// Server closes the socket with this code as the final step of ending a usage - see
// isFinalCloseCode() below.
const USAGE_ENDED_CLOSE_CODE = 4001;

@Injectable({
  providedIn: 'root'
})
export class WsDemoConnectorService extends WsConnectorServiceBase<WsDemoPushDto> {
  protected override readonly wsPath = 'demo';
  protected override readonly logLabel = 'Demo WebSocket';

  private store = inject(Store);

  // "Usage ended" can arrive two ways - the USAGE_ENDED push (carries endedAt) or a bare
  // 4001 close (the push itself can be lost, send failures are swallowed server-side).
  // Whichever comes first wins; this dedupes so consumers see exactly one emission.
  private readonly _usageEnded$ = new Subject<string | undefined>();
  readonly usageEnded$ = this._usageEnded$.asObservable();
  private hasSignaledUsageEnded = false;

  override async initSocket() {
    this.hasSignaledUsageEnded = false;
    await super.initSocket();
  }

  protected override isFinalCloseCode(code: number): boolean {
    return code === USAGE_ENDED_CLOSE_CODE;
  }

  protected override onFinalClose(): void {
    this.signalUsageEnded(undefined);
  }

  protected override handleMessage(message: WsDemoPushDto) {
    switch (message.type) {
      case DemoPushType.SCENARIO_STARTED:
        this.store.dispatch(ScenarioActions.public.scenarioStartedFromWebSocket(message.content));
        break;
      case DemoPushType.SCENARIO_STOPPED:
        this.store.dispatch(ScenarioActions.public.scenarioStoppedFromWebSocket({
          scenarioId: message.content.scenarioId,
        }));
        break;
      case DemoPushType.USAGE_ENDED:
        this.signalUsageEnded(message.content.endedAt);
        break;
    }
  }

  private signalUsageEnded(endedAt: string | undefined) {
    if (this.hasSignaledUsageEnded) {
      return;
    }

    this.hasSignaledUsageEnded = true;
    this._usageEnded$.next(endedAt);
  }
}
